import styles from './WebshopRequestController.module.scss';

import { Button, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogActions, DialogContent } from '@work4all/components';
import { useActiveInterval } from '@work4all/components/lib/hooks/use-active-interval';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { WebShopRequest } from '@work4all/models/lib/Classes/WebShopRequest.entity';

import { formatAsFloat } from '@work4all/utils';

import { useShadowBzObjectApiContext } from '../../../../../hooks/use-bz-shadow-object-api/use-shadow-bz-object-api-context';

import { useWebshopApi } from './hooks/use-webshop-api';
import { WebShopRequestResponseArticle } from './types';

export interface WebShopRequestResponse {
  Articles: Record<string, WebShopRequestResponseArticle>;
}

const refreshInterval = 2000;

export const WebShopRequestController = (props: {
  skip: boolean;
  onResultCompleted: () => void;
}) => {
  const { skip, onResultCompleted } = props;
  const [dialogOpen, setDialogOpen] = useState(false);

  const shadowBzObjectApi = useShadowBzObjectApiContext();
  const shadowBzObjectId = shadowBzObjectApi.initialShadowObject?.id;

  const { t } = useTranslation();

  const { getWebshopRequestsCompleted, deleteWebshopRequest } = useWebshopApi();

  const [webShopCompletedData, setWebShopCompletedData] =
    useState<WebShopRequest | null>(null);
  const [overrideSkip, setOverrideSkip] = useState(false);

  const refetch = useCallback(async () => {
    const result = await getWebshopRequestsCompleted(shadowBzObjectId);
    if (result.data?.getWebshopRequestsCompleted?.response) {
      setWebShopCompletedData(result.data.getWebshopRequestsCompleted);
      setOverrideSkip(true);
      if (!(skip || overrideSkip)) {
        setDialogOpen(true);
      }
    }
  }, [getWebshopRequestsCompleted, overrideSkip, shadowBzObjectId, skip]);

  useActiveInterval(refetch, refreshInterval, skip || overrideSkip);

  const response = useMemo(() => {
    const _data = webShopCompletedData as WebShopRequest;
    return _data?.response
      ? (JSON.parse(_data.response) as WebShopRequestResponse)
      : null;
  }, [webShopCompletedData]);

  const handleDeleteRequest = useCallback(async () => {
    await deleteWebshopRequest(shadowBzObjectId);
    setOverrideSkip(false);
    setDialogOpen(false);
  }, [deleteWebshopRequest, shadowBzObjectId]);

  const resolveRequest = useCallback(async () => {
    await shadowBzObjectApi.resolveWebShopRequest({
      onComplete: () => {
        handleDeleteRequest();
        onResultCompleted();
        setOverrideSkip(false);
      },
    });
  }, [handleDeleteRequest, onResultCompleted, shadowBzObjectApi]);

  const handleConfirm = useCallback(async () => {
    if (!response?.Articles) {
      return;
    }
    resolveRequest();
    setDialogOpen(false);
  }, [resolveRequest, response?.Articles]);

  return (
    <Dialog
      open={dialogOpen}
      title={t('WEBSHOP.SUCCESS.TITLE')}
      classes={{ dialog: { paper: styles.dialog } }}
      onClose={() => setDialogOpen(false)}
    >
      <DialogContent>
        <table cellPadding={remToPx(0.25)}>
          {response?.Articles &&
            Object.keys(response?.Articles)?.map((key) => {
              const article = response.Articles[key];
              const singlePrice = article.Price / article.PriceUnit;
              const totalPrice = singlePrice * article.Quantity;
              return (
                <tr>
                  <td style={{ textAlign: 'right' }}>
                    <Typography color="var(--text03)">
                      {article.Quantity}
                    </Typography>
                  </td>
                  <td>
                    <Typography color="var(--text03)">
                      {article.Unit}
                    </Typography>
                  </td>
                  <td>
                    <Typography>{article.Description}</Typography>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Typography color="var(--text03)">
                      {formatAsFloat(totalPrice, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </td>
                  <td>
                    <Typography color="var(--text03)">
                      {article.Curreny}
                    </Typography>
                  </td>
                </tr>
              );
            })}
        </table>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteRequest}>{t('ALERTS.DISCARD')}</Button>
        <Button onClick={handleConfirm}>{t('COMMON.APPLY')}</Button>
      </DialogActions>
    </Dialog>
  );
};
